import { Linkable } from '@/components/blocks/Linkable';
import { Img } from '@/components/blocks/MaterialImage';
import { OPTION_TYPES } from '@/components/content/Header/constants';
import { autocompleteSX } from '@/components/content/Header/styles/search';
import DynamicIcon, { IconName } from '@/components/icons/DynamicIcon';
import { HDM_SEARCH_SEO_PATHS } from '@/data/constants/urlPathConstants';
import { SuggestOption } from '@/data/types/Search';
import { Box, Typography } from '@mui/material';
import {
	EVENT_SEARCH_SUBMITTED_TERM_KEY,
	EVENT_SEARCH_TYPE_KEY,
	GA4SearchType,
	SEARCH_TYPE,
	decodeUrlDestination,
	useTrackSearchSubmit,
} from 'presentation/events/hooks/useTrackSearchSubmit';
import React, { useState } from 'react';
import { NOT_AVAILABLE_IMAGE, OpenSansFontWeights } from 'shared/custom/constants/commonConstants';
import { capitalizeFirstLetter, getImageURL, saveSearchTerm } from '../utils/search';

type HighlightedPart = {
	text: string;
	bold: boolean;
};

type AutocompleteOptionProps = {
	option: SuggestOption;
	parts: HighlightedPart[];
	icon: string;
	props: React.HTMLAttributes<HTMLLIElement>;
	onSubmit?: (entry: { label: string; href?: string }) => void | Promise<void>;
};

export const AutocompleteOption = ({
	option,
	parts,
	icon,
	props,
	onSubmit,
}: AutocompleteOptionProps) => {
	const { sendGA4Event, sendSocialMediaEvents } = useTrackSearchSubmit();

	const isRecentProduct = option.identifier === OPTION_TYPES.RecentProduct;
	const isCategory = option.identifier === OPTION_TYPES.Category;
	const isKeywordOrBrand =
		option.identifier === OPTION_TYPES.Keyword || option.identifier === OPTION_TYPES.Brand;
	const isRecentSearch = option.identifier === OPTION_TYPES.RecentSearch;
	const runsThroughOnSubmit = isKeywordOrBrand || isRecentSearch;

	const [imageSrc, setImageSrc] = useState(
		isRecentProduct ? getImageURL(option.sku, option.href) : undefined
	);

	// Keyword/Brand/RecentSearch suggestions run through `onSubmit` so the searchdisplay/
	// bySearchTerm redirectUrl lookup (already used for typed submits) is also applied on click.
	const submitSuggestion = () => {
		const searchType: GA4SearchType =
			option.identifier === OPTION_TYPES.Brand
				? SEARCH_TYPE.SUGGESTED_BRAND
				: isRecentSearch
				? SEARCH_TYPE.RECENT_SEARCH
				: SEARCH_TYPE.SUGGESTED_TERM;
		sessionStorage.setItem(EVENT_SEARCH_TYPE_KEY, searchType);
		void onSubmit?.({ label: option.label, href: option.href });
	};

	const handleSelect = () => {
		sessionStorage.setItem(EVENT_SEARCH_SUBMITTED_TERM_KEY, option.label);
		let searchType: GA4SearchType = SEARCH_TYPE.SUGGESTED_TERM;
		if (option.identifier === OPTION_TYPES.Brand) {
			searchType = SEARCH_TYPE.SUGGESTED_BRAND;
		} else if (option.identifier === OPTION_TYPES.Category) {
			searchType = SEARCH_TYPE.SUGGESTED_LANDING;
		} else if (option.identifier === OPTION_TYPES.RecentProduct) {
			searchType = SEARCH_TYPE.RECENT_VIEWED;
		} else if (option.identifier === OPTION_TYPES.RecentSearch) {
			searchType = SEARCH_TYPE.RECENT_SEARCH;
		}

		sessionStorage.setItem(EVENT_SEARCH_TYPE_KEY, searchType);

		sendSocialMediaEvents({ searchTerm: option.label });
		if (!isRecentProduct && !isCategory) {
			saveSearchTerm(option.label);
		}

		const goesToSearchResultsPage =
			!option.href || option.href.startsWith(`/${HDM_SEARCH_SEO_PATHS.Search}/`);
		if (!goesToSearchResultsPage) {
			sessionStorage.removeItem(EVENT_SEARCH_SUBMITTED_TERM_KEY);
			sessionStorage.removeItem(EVENT_SEARCH_TYPE_KEY);
			sendGA4Event({
				searchTerm: option.label,
				searchType,
				urlDestination: decodeUrlDestination(window.location.origin + option.href),
				numberOfResults: -1,
			});
		}
	};

	const handleClick = (e: React.MouseEvent<HTMLLIElement>) => {
		if (runsThroughOnSubmit && onSubmit) {
			e.preventDefault();
			submitSuggestion();
			props.onClick?.(e);
			return;
		}
		handleSelect();
		props.onClick?.(e);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
		if (e.key === 'Enter') {
			if (runsThroughOnSubmit && onSubmit) {
				e.preventDefault();
				submitSuggestion();
				props.onKeyDown?.(e);
				return;
			}
			handleSelect();
		}
		props.onKeyDown?.(e);
	};

	return (
		<Linkable
			href={option.href}
			id={option.href}
			data-testid={option.href}
			{...props}
			sx={isRecentProduct ? autocompleteSX.recentProductLink : autocompleteSX.optionContainer}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
		>
			{isRecentProduct ? (
				<Box sx={autocompleteSX.recentProductContainer}>
					<Img
						src={imageSrc}
						alt={option.label}
						sx={autocompleteSX.recentProductImage}
						onError={() => setImageSrc(NOT_AVAILABLE_IMAGE)}
					/>
					<Box sx={autocompleteSX.recentProductNameContainer}>
						<Typography variant="bodySubText" fontWeight={OpenSansFontWeights.semibold}>
							{capitalizeFirstLetter(option?.brand ?? '')}
						</Typography>
						<Typography variant="bodySubText" sx={autocompleteSX.recentProductName}>
							{capitalizeFirstLetter(option.label)}
						</Typography>
						<Typography variant="bodyCaption">SKU {option.sku}</Typography>
					</Box>
				</Box>
			) : (
				<>
					<DynamicIcon name={icon as IconName} sx={autocompleteSX.optionIcon} />
					<Box sx={autocompleteSX.optionKeyword}>
						{parts.map((part) => (
							<Typography
								key={`${option.identifier}-${part.text}`}
								component="span"
								variant="bodyBaseline"
								sx={autocompleteSX.optionLabel}
								fontWeight={part.bold ? OpenSansFontWeights.bold : OpenSansFontWeights.regular}
							>
								{part.text}
							</Typography>
						))}
					</Box>
				</>
			)}
		</Linkable>
	);
};
